import { useTranslations } from '@vocab/react';
import { Button, IconAI, Stack } from 'braid-design-system';
import { useCallback } from 'react';

import { CareerFeedAIAssistantContentFrame } from '../CareerFeedAIAssistantContent/CareerFeedAIAssistantContentFrame';
import { PrivacyAndCollectionNotice } from '../CareerFeedAIAssistantContent/PrivacyAndCollectionNotice';
import { CareerFeedMobileWebFeatureFlagContainer } from '../CareerFeedMobileWebFeatureFlagContainer';
import { useCareerFeedAIAssistant } from '../hooks/useCareerFeedAIAssistant';
import { useRecommendationForCareerFeedAiAssistant } from '../hooks/useRecommendationForCareerFeedAiAssistant';
import type { ErrorType } from '../types';

import translations from './.vocab';
import { CareerFeedAIAssistantFAB } from './CareerFeedAIAssistantFAB';

const CareerFeedAiAssistantInitialStateView = ({
  isButtonLoading,
  errorType,
  onButtonClick,
}: {
  isButtonLoading: boolean;
  errorType?: ErrorType | null;
  onButtonClick: () => void;
}) => {
  const { t } = useTranslations(translations);

  return (
    <CareerFeedAIAssistantContentFrame
      headMessage={{
        text: t(
          "Tell seekAi what you're looking for so we can suggest more relevant jobs.",
        ),
        size: 'standard',
      }}
      errorType={errorType}
      ctaSection={
        <Stack space="small">
          <Button
            tone="brandAccent"
            icon={<IconAI />}
            loading={isButtonLoading}
            onClick={onButtonClick}
          >
            {t('Refine recommendations')}
          </Button>
          <PrivacyAndCollectionNotice />
        </Stack>
      }
      loading={isButtonLoading}
    />
  );
};

export const CareerFeedAiAssistantInitialState = () => {
  const {
    invokeCreateSession,
    isCreateSessionLoading,
    createSessionError,
    tracking: { trackGenAIChatStartPressed },
  } = useCareerFeedAIAssistant();
  const { jobIds: recommendedJobIds } =
    useRecommendationForCareerFeedAiAssistant();

  const startSession = useCallback(() => {
    if (!isCreateSessionLoading) {
      trackGenAIChatStartPressed();
      invokeCreateSession(recommendedJobIds);
    }
  }, [
    invokeCreateSession,
    isCreateSessionLoading,
    recommendedJobIds,
    trackGenAIChatStartPressed,
  ]);

  return (
    <CareerFeedMobileWebFeatureFlagContainer
      mobileView={
        <CareerFeedAIAssistantFAB
          isButtonLoading={isCreateSessionLoading}
          onButtonClick={startSession}
        />
      }
      tabletAndAboveView={
        <CareerFeedAiAssistantInitialStateView
          isButtonLoading={isCreateSessionLoading}
          errorType={createSessionError}
          onButtonClick={startSession}
        />
      }
    />
  );
};
